<template>
	<v-row
		no-gutters
		v-if="localProfileData"
		align="center"
		class="list_item pt-3"
		@keyup.esc.prevent="handleCancel"
		@keyup.enter.prevent="editNameMode = false"
	>
		<v-col cols="auto">
			<v-card class="ac_card integration inline-block">
				<div class="centerdXY">
					<SIcon :width="18" :name="currentApp.service_slug" v-if="currentApp" />
				</div>
			</v-card>
		</v-col>
		<v-col class="pl-3">
			<h3 class="d_name light">
				<span v-if="!editNameMode" @click="editName" style="cursor: pointer" :class="{ 'is-disabled': !canChangeConnection }">{{
					localProfileData.connection_name
				}}</span>
				<div v-show="editNameMode">
					<v-text-field
						hide-details
						single-line
						filled
						dense
						@blur="saveConnectionName"
						class="edit_name_tf mb-2 srch"
						ref="editName"
						v-model="localProfileData.connection_name"
					></v-text-field>
				</div>
				<v-btn v-if="canChangeConnection" icon fab x-small rounded class="edit_name" @click="editName">
					<SIcon name="edit-name" iconColor="#959DA1" style="left: -2px"></SIcon>
				</v-btn>
			</h3>
			<p class="display-flex flex-row flex-align-center">
				<ProjectChip
					:alwaysShowProjectChip="alwaysShowProjectChip"
					:projectId="localProfileData.access?.related_entity_id || personalProject?.id"
				/>
				Connected&nbsp; <timeago :datetime="localProfileData.connected_at" :auto-update="60" />&nbsp;Used in&nbsp;
				<v-menu :close-on-content-click="false">
					<template v-slot:activator="{ on, attrs }">
						<span
							v-bind="attrs"
							v-on="on"
							@click="getWorkflowsByCredentials(localProfileData.id)"
							:class="['flows_list', { unactive: !usedInWorkflowsCount }]"
							style="text-decoration: none; font-weight: 500"
							>{{ usedInWorkflowsText }}</span
						>
					</template>
					<v-list>
						<template v-for="(flow, index) in usedInWorkflows">
							<v-list-item dense :key="`flow-${index}`" v-if="workflowsByCredentialsLoader"> Loading... </v-list-item>
							<v-divider v-if="flow.divider && !workflowsByCredentialsLoader" :key="`divider-${index}`"></v-divider>
							<v-list-item
								dense
								v-else-if="!workflowsByCredentialsLoader"
								:key="flow.workflowId"
								:to="
									flow.header
										? { name: 'Features', params: { filter: mappedFlowsCredentialsUsedIn.map((x) => x.workflowId) } }
										: { name: 'Feature', params: { wfID: flow.workflowId, pID: flow.projectId } }
								"
							>
								<v-list-item-title else>{{ flow.workflowTitle }}</v-list-item-title>
							</v-list-item>
						</template>
					</v-list>
				</v-menu>
			</p>
		</v-col>

		<v-col class="text-right">
			<SIcon class="u_icon" :name="isProjectConnection ? 'users' : 'users-lock'" />
			<div class="test_wrpr text-right" :class="{ t_failed: needAttention }">
				<AnemicButton
					v-if="!tested && !needAttention"
					:disabled="!canChangeConnection"
					:loading="isTesting"
					outlined
					tile
					@click="testProfile"
					class="test-button"
				>
					Test
				</AnemicButton>

				<div v-if="tested && !needAttention" class="succ">
					<span class="succ">Success</span>
				</div>
				<div v-if="needAttention" class="failed">
					<span class="pr-3">Not Connected</span>
					<v-btn text tile class="mr-2 det_btn" color="#E94E4E" height="20" @click="showDetailsDialog = true">Details</v-btn>
					<v-btn
						text
						tile
						style="background: rgba(92, 141, 245, 0.15); font-size: 12px !important; padding: 2px 10px"
						color="#5C8DF5"
						height="22"
						:disabled="!canChangeConnection"
						@click="reconnectProfile"
						>Reconnect</v-btn
					>
				</div>
				<v-menu :disabled="menuItems.length === 0" absolute right bottom :nudge-bottom="10">
					<template v-slot:activator="{ on, attrs }">
						<AnemicButton v-bind="attrs" v-on="on" width="22" height="22" icon fab x-small class="flex-centerize menu-button ml-1">
							<SIcon name="three-dot-menu" width="13" class="menu-icon" />
						</AnemicButton>
					</template>
					<v-list>
						<template v-for="(item, i) in menuItems">
							<v-list-item :key="i" v-if="!item.hide">
								<v-list-item-title
									><v-btn text @click="item.onClick" :loading="item.loading">{{ item.text }}</v-btn></v-list-item-title
								>
							</v-list-item>
						</template>
					</v-list>
				</v-menu>
			</div>
		</v-col>
		<v-col cols="12" class="mt-3 mb-1"> </v-col>
		<ProfilesErrorDetailsDialog :showDialog.sync="showDetailsDialog" :errorMsg="localProfileData.error_message" />
		<AddProfile v-if="activeReconnect" v-bind="addProfileBindData" ref="profileManager" />
	</v-row>
</template>

<script>
import { projectsList } from '@/store/modules/projects/getters';
import ProjectChip from '@/views/Flow/ProjectChip.vue';
import { mapActions, mapGetters } from 'vuex';
import { getService } from '@/store/modules/apps/getters';
import { appURL, appApiURL } from '@/store/modules/main/getters';
import {
	getUserCredentials,
	deleteUserCredentials,
	testUserCredentials,
	updateUserCredentialsName,
} from '@/store/modules/credentials/actions';
import ProjectService from '@/utils/projects-service';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { getWorkflowsForCredentials } from '@/store/modules/workflows/actions';
import { getWorkflowData } from '@/store/modules/workflows/actions';
import { getWorkflow } from '@/store/modules/workflows/getters';
import { selectedProjectId } from '@/store/modules/selectedProject/getters';
import { buildProfileObj } from '@/utils/helpers';
import { ALL_PROJECTS_VIRTUAL_ID } from '@/utils/projects.const';
import CredentialsService from '@/utils/credentials-service';
import { projectHasPermission } from '@/store/modules/projects/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';
import { UserCredentialsStatus, ProjectPermissions } from '@ply-io/ply-front-shared-utilities';
import { pluralize } from '@/utils/language-service';
export default {
	name: 'SettingsProfilesListItem',
	components: { ProjectChip },
	props: {
		profileData: { type: Object },
		alwaysShowProjectChip: { type: Boolean },
	},
	computed: {
		...mapGettersShorthand({ projectsList }),
		...mapGetters(['user']),
		...mapGetters([
			appURL.fullPath,
			appApiURL.fullPath,
			getService.fullPath,
			selectedProjectId.fullPath,
			projectHasPermission.fullPath,
			getWorkflow.fullPath,
		]),
		personalProject() {
			return this.projectsList.find((x) => ProjectService.isMyPersonalProject(x, this.user)); // this is a find, instead of filter, because we limited the personal projects to be only one
		},
		needAttention() {
			return this.localProfileData ? this.localProfileData.status === UserCredentialsStatus.Staled : false;
		},
		isProjectConnection() {
			return !CredentialsService.isConnectionPersonal(this.profileData);
		},
		currentApp() {
			return this[getService.fullPath](this.localProfileData.service);
		},
		addProfileBindData() {
			return buildProfileObj(this.currentApp);
		},
		selectedProjectId() {
			return this[selectedProjectId.fullPath];
		},
		showSettingsBtn() {
			return this.localProfileData.id && this.canChangeConnection;
		},
		canChangeConnection() {
			if (this.selectedProjectId === ALL_PROJECTS_VIRTUAL_ID) {
				return true;
			}
			return this[projectHasPermission.fullPath](this.selectedProjectId, ProjectPermissions.ChangeCredentials);
		},
		menuItems() {
			return [
				{ hide: !this.showSettingsBtn, onClick: this.openCredentialsSetting, text: 'Settings', loading: false },
				{ hide: !this.canChangeConnection, onClick: this.disconnectProfile, text: 'Disconnect', loading: this.loadingDisconnect },
				{ hide: !this.canChangeConnection, onClick: this.reconnectProfile, text: 'Reconnect Profile', loading: false },
			];
		},
		usedInWorkflowsCount() {
			return this.localProfileData.used_in_workflows?.length || 0;
		},
		usedInWorkflowsText() {
			return pluralize('feature', this.usedInWorkflowsCount, true);
		},
		mappedFlowsCredentialsUsedIn() {
			if (!this.localProfileData.used_in_workflows) {
				return [];
			}
			return this.localProfileData.used_in_workflows.map(({ workflow: workflowId, project: projectId }) => ({
				workflowId,
				projectId,
				workflowTitle: this[getWorkflow.fullPath](workflowId)?.workflow_title || `Module ${workflowId}`,
			}));
		},
		usedInWorkflows() {
			return this.mappedFlowsCredentialsUsedIn;
		},
	},
	methods: {
		...mapActions([
			getUserCredentials.fullPath,
			deleteUserCredentials.fullPath,
			testUserCredentials.fullPath,
			updateUserCredentialsName.fullPath,
			getWorkflowData.fullPath,
			getWorkflowsForCredentials.fullPath,
		]),
		getAppProfiles() {
			this[getUserCredentials.fullPath]({ forceGet: true });
		},
		editName() {
			if (!this.canChangeConnection) {
				return;
			}
			this.editNameMode = true;
			this.$nextTick(() => {
				this.$refs.editName.focus();
			});
		},
		handleCancel() {
			this.editNameMode = false;
			this.copyProfileData();
		},
		async disconnectProfile() {
			if (!confirm('Are you sure you want to disconnect this connection?')) {
				return;
			}
			this.loadingDisconnect = true;
			await this[deleteUserCredentials.fullPath]({ id: this.localProfileData.id }).finally(() => {
				this.loadingDisconnect = false;
			});
		},
		reconnectProfile() {
			this.activeReconnect = true;
			this.$nextTick(() => {
				this.$refs.profileManager.startAddProfile({
					credsID: this.localProfileData.id,
					app: this.currentApp,
					apiURL: this[appApiURL.fullPath],
					appURL: this[appURL.fullPath],
					getAppProfiles: this.getAppProfiles,
					version: this.currentApp.version,
				});
				this.tested = true;
			});
		},
		openCredentialsSetting() {
			AppDialogManagerService.openUpdateProjectConnectionDialog({
				projectId: !this.selectedProjectId || isNaN(this.selectedProjectId) ? null : Number(this.selectedProjectId),
				credentialsId: this.localProfileData.id,
				initialData: this.localProfileData,
			});
		},
		async testProfile() {
			this.isTesting = true;
			await this[testUserCredentials.fullPath]({ id: this.localProfileData.id });
			this.isTesting = false;
			this.tested = true;
		},
		async saveConnectionName() {
			this.editNameMode = false;
			await this[updateUserCredentialsName.fullPath]({ id: this.localProfileData.id, name: this.localProfileData.connection_name });
		},
		copyProfileData() {
			this.$set(this, 'localProfileData', { ...this.profileData });
			// this.usedInWorkflows.forEach((wf) => this[getWorkflowData.fullPath]({ id: wf.workflowId, forceGet: false, forceGetNodes: false })); // retrieve workflows names if we dont have them
		},
		async getWorkflowsByCredentials(profileId) {
			this.workflowsByCredentialsLoader = true;
			await this[getWorkflowsForCredentials.fullPath]({ cid: profileId });
			this.workflowsByCredentialsLoader = false;
		},
	},

	watch: {
		profileData: {
			handler(newV) {
				if (newV) {
					this.activeReconnect = false;
					this.copyProfileData();
				}
			},
			deep: true,
			immediate: true,
		},
	},
	data: () => ({
		showDetailsDialog: false,
		activeReconnect: false,
		tested: false,
		testResult: {
			error: 'Sorry. You have exceeded the API rate limit, please try again later.',
			tested: false,
			isSuccess: null,
		},
		isTesting: false,
		editNameMode: false,
		localProfileData: null,
		loadingDisconnect: false,
		workflowsByCredentialsLoader: false,
	}),
};
</script>

<style scoped lang="scss">
.ac_card {
	width: 43px;
	height: 43px !important;
	border-color: #e1e1e1 !important;
}
p {
	font-size: 12px;
	color: #919191;
	line-height: 15px;
}
.menu_btn {
	border: 1px solid #959da1 !important;
	width: 25px;
	height: 25px;
	background-color: var(--page-background);
	transition: all 0.2s;
	opacity: 0;
	position: absolute;
	margin-left: 3px;
}
.edit_name {
	width: 21px;
	height: 21px;
	opacity: 0;
	background: #f0f1f3 !important;
	transition: 0.2s;
	position: absolute;
	right: 0px;
	top: 0px;
}
.list_item {
	background: var(--bg-color);
	&:hover {
		transition: 0.1s;
		background: #f5f5f5;
	}
}
.menu-button {
	background: transparent !important;
	&:hover {
		border: 1px solid var(--forth-border-color);
	}
	.menu-icon {
		margin-top: -6px;
		margin-left: -2px;
	}
	position: relative;
	display: inline-block;
}
.list_item:hover .menu_btn {
	opacity: 1;
}
.list_item:hover .edit_name {
	opacity: 1;
}
.u_icon {
	transform: scale(0.8);
	display: inline-block;
	vertical-align: middle;
}
.test_btn {
	text-transform: capitalize !important;
	font-weight: 400 !important;
	font-size: 14px !important;
	border-radius: 2px !important;
}
span.flows_list:not(.unactive):hover {
	color: #5c8df5 !important;
}
span.flows_list.unactive {
	cursor: default;
	pointer-events: none;
}
.edit_name_tf {
	position: relative;
	width: 300px;
	top: 0px;
	left: 0px;
}
.d_name {
	line-height: 18px;
	position: relative;
	display: inline-block;
	padding-right: 30px;
}
.d_name span {
	margin-top: 3px;
	position: relative;
}

.det_btn {
	background: rgba(233, 78, 78, 0.15) !important;
	font-size: 12px !important;
	padding: 1px 10px !important;
}

.test_wrpr {
	position: relative;
	display: inline-block;
	width: 100px;
	height: 26px;
	vertical-align: middle;
	font-size: 14px;
}
.test_wrpr.t_failed {
	width: 270px;
}
/*.test_wrpr.failed{width: 350px}*/
.succ {
	position: absolute;
	left: 0px;
	top: 0px;
	width: 100%;
	text-align: center;
}
.succ span {
	color: #1bbc9c;
	font-size: 14px;
}
.failed {
	position: absolute;
	left: 0px;
	top: 0px;
	width: 100%;
	text-align: right;
}
.failed span {
	color: #e94e4e;
}
.test-button {
	width: 70px !important;
	height: 26px !important;
	border-radius: 8px !important;
	color: var(--bold-border-color);
	background: transparent !important;
	border: 1px solid var(--bold-border-color);
}
</style>
